import { buildingGetConditionedArea } from "building-generation/elements/floorAreaTakeoffs/buildingGetConditionedArea";
import type { Building } from "building-types/building/buildingTypes";
import type { ShowroomBuildingStats } from "building-types/building/showroomTypes";
import { getSourceRoomId } from "building-types/components/componentInstanceUtils";
import type { Room } from "building-types/rooms/roomTypes";
import type { FloorAreaTakeOff } from "building-types/rooms/takeoffTypes";
import getBedBathCount from "doc/getBedBathCount";
import getGarageCount, { getRoomGarageCount } from "doc/getGarageCount";

export default function getShowroomStats(building: Building): ShowroomBuildingStats {
  const { bedroomCount, bathroomCount } = getBedBathCount(building.rooms);
  return {
    beds: bedroomCount,
    baths: bathroomCount,
    sqft: buildingGetConditionedArea(building) || building.totalFloorArea || 0,
    garageCount: getGarageCount(building),
  };
}

/**
 * Returns the building rooms that belong to a reporting unit, resolved from both rooms with the
 * unit label applied directly (original rooms) and rooms whose component instance carries the
 * label. The returned rooms' `id`s are building room ids (matching `building.rooms` keys,
 * including the `__ci__` suffix for instanced rooms).
 */
export function getRoomsForReportingUnit(building: Building, selectedReportingUnitId: string): Room[] {
  return [
    ...roomsByDirectLabel(building, selectedReportingUnitId),
    ...getRoomsByComponentInstance(building, selectedReportingUnitId),
  ];
}

export function getShowroomStatsForUnit(building: Building, selectedReportingUnitId: string): ShowroomBuildingStats {
  const allRooms = getRoomsForReportingUnit(building, selectedReportingUnitId);

  if (allRooms.length === 0) {
    return { beds: 0, baths: 0, garageCount: 0, sqft: 0 };
  }

  return statsFromRooms(building, allRooms);
}

function roomsByDirectLabel(building: Building, unitId: string): Room[] {
  const { rooms: docRooms } = building.doc;
  const result: Room[] = [];

  for (const id in building.rooms) {
    const room = building.rooms[id];
    const sourceRoomId = room.sourceRoomId ?? id;
    const docRoom = docRooms[sourceRoomId];
    if (docRoom?.labels[unitId]) {
      result.push(room);
    }
  }
  return result;
}

/**
 * Returns rooms whose component instance chain maps to the given reporting unit.
 * Building room IDs for instanced rooms use the format `sourceRoomId__ci__instanceId`.
 * We check if the top-level instance in that chain carries the reporting unit label.
 */
function getRoomsByComponentInstance(building: Building, unitId: string): Room[] {
  const componentInstances = building.doc.componentInstances;

  const result: Room[] = [];

  for (const [instanceId, instance] of Object.entries(componentInstances)) {
    if (instance.labels[unitId]) {
      const rooms = getComponentRooms(building, instance.componentId, instanceId);

      result.push(...rooms);
    }
  }

  return result;
}

function getComponentRooms(building: Building, componentId: string, instanceId: string): Room[] {
  const ROOM_PREFIX = "room.";

  const docRoomIds = new Set<string>();
  for (const hash in building.doc.componentMembers) {
    if (hash.startsWith(ROOM_PREFIX) && building.doc.componentMembers[hash] === componentId) {
      docRoomIds.add(hash.slice(ROOM_PREFIX.length));
    }
  }

  const result: Room[] = [];
  for (const id in building.rooms) {
    const room = building.rooms[id];
    const sourceRoomId = getSourceRoomId(room);
    if (docRoomIds.has(sourceRoomId) && (!room.instanceId || room.instanceId === instanceId)) {
      result.push(room);
    }
  }

  return result;
}
function statsFromRooms(building: Building, rooms: Room[]): ShowroomBuildingStats {
  let beds = 0;
  let baths = 0;
  let garageCount = 0;
  const levelIds = new Set<string>();

  for (const room of rooms) {
    beds += room.attributes?.bedroomCount ?? 0;
    baths += room.attributes?.bathroomCount ?? 0;
    garageCount += getRoomGarageCount(building, room);
    levelIds.add(room.level.id);
  }

  // Level IDs are unique per reporting unit — each unit occupies distinct levels, so summing
  // level floor areas won't accidentally include area from another unit.
  const sqft =
    sumFloorAreaForLevels(building.floorAreaTakeoffs?.CONDITIONED_FLOOR_AREA, levelIds) ||
    sumFloorAreaForLevels(building.floorAreaTakeoffs?.TOTAL_FLOOR_AREA, levelIds);

  return { beds, baths, garageCount, sqft };
}

function sumFloorAreaForLevels(takeoff: FloorAreaTakeOff | undefined, levelIds: Set<string>): number {
  if (!takeoff?.levelFloorAreas) {
    return 0;
  }
  let area = 0;
  for (const levelId of levelIds) {
    area += takeoff.levelFloorAreas[levelId]?.floorArea ?? 0;
  }
  return area;
}
